import { Link } from '@tanstack/react-router'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { TooltipProps } from 'recharts'

import { StatusPill } from '@/components/status-pill'
import { AXIS_TICK, compactMoney, GRID_COLOR, num } from '@/features/home/model'
import { sentenceCase, type StatusTone } from '@/lib/status-tones'
import { cn, formatMoney, formatNumber, formatPercent, parseUTCDate } from '@/lib/utils'

import type { CustomerInsights, CustomerPayments } from './api'
import { localMonth, monthDate, monthPoints, type MonthPoint } from './customer-months'

type BreakdownRow = CustomerInsights['manufacturers']['rows'][number]
type PaymentMethod = CustomerInsights['payments']['methods'][number]['method']
type PaymentIssue = CustomerInsights['payments']['issues'][number]

// The Analytics page's series colors, so the two pages read alike.
const SERIES = [
  { key: 'revenue', label: 'Revenue', color: '#2a78d6' },
  { key: 'net', label: 'Net', color: '#eb6834' },
  { key: 'profit', label: 'Profit', color: '#1baf7a' },
] as const
const MARGIN_COLOR = '#1baf7a'
const ORDERS_COLOR = '#2a78d6'
const PROJECTS_COLOR = '#6250d6'

const METHODS: Record<PaymentMethod, { label: string; color: string }> = {
  card: { label: 'Card', color: '#2a78d6' },
  bank: { label: 'Bank transfer', color: '#1baf7a' },
  check: { label: 'Check', color: '#eb6834' },
  wire: { label: 'Wire', color: '#6250d6' },
  cash: { label: 'Cash', color: '#0e9fb4' },
  financing: { label: 'Financing', color: '#d6409f' },
  other: { label: 'Other', color: '#9ca3af' },
}

const monthLong = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

function Panel({
  title,
  aside,
  className,
  children,
}: {
  title: string
  aside?: React.ReactNode
  className?: string
  children: React.ReactNode
}) {
  return (
    <section aria-label={title} className={cn('min-w-0 rounded-[14px] border bg-card p-4 sm:p-5', className)}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h2 className="text-base font-semibold">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  )
}

function Legend({ items }: { items: ReadonlyArray<{ label: string; color: string }> }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {items.map((item) => (
        <li className="flex items-center gap-1.5" key={item.label}>
          <span aria-hidden className="h-2.5 w-2.5 rounded-[3px]" style={{ background: item.color }} />
          {item.label}
        </li>
      ))}
    </ul>
  )
}

function MonthTooltip({ active, payload }: TooltipProps<number, string>) {
  const point = active ? (payload?.[0]?.payload as MonthPoint | undefined) : undefined
  if (!point) return null
  return (
    <div className="min-w-44 rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
      <p className="mb-1.5 font-medium">{monthLong.format(monthDate(point.month))}</p>
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 tabular-nums">
        {SERIES.map((series) => (
          <div className="contents" key={series.key}>
            <dt className="flex items-center gap-1.5 text-muted-foreground">
              <span aria-hidden className="h-2 w-2 rounded-[2px]" style={{ background: series.color }} />
              {series.label}
            </dt>
            <dd className="text-right">{formatMoney(point[series.key])}</dd>
          </div>
        ))}
        <dt className="text-muted-foreground">Margin</dt>
        <dd className="text-right">{formatPercent(point.margin)}</dd>
        <dt className="text-muted-foreground">Orders</dt>
        <dd className="text-right">{point.orders}</dd>
        <dt className="text-muted-foreground">Average order</dt>
        <dd className="text-right">{formatMoney(point.averageOrder)}</dd>
        <dt className="text-muted-foreground">Projects</dt>
        <dd className="text-right">{point.projects}</dd>
      </dl>
    </div>
  )
}

const xAxis = <XAxis axisLine={false} dataKey="label" minTickGap={8} tick={AXIS_TICK} tickLine={false} />
const grid = <CartesianGrid stroke={GRID_COLOR} vertical={false} />

function SalesChart({ points }: { points: MonthPoint[] }) {
  return (
    <div className="h-[260px] w-full">
      <ResponsiveContainer height="100%" width="100%">
        <BarChart barCategoryGap="22%" barGap={2} data={points} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          {grid}
          {xAxis}
          <YAxis
            axisLine={false}
            tick={AXIS_TICK}
            tickFormatter={(value: number) => compactMoney.format(value)}
            tickLine={false}
            width={56}
          />
          <ReferenceLine stroke="oklch(0.708 0 0)" y={0} />
          <Tooltip content={<MonthTooltip />} cursor={{ fill: 'oklch(0.97 0 0)' }} />
          {SERIES.map((series) => (
            <Bar
              dataKey={series.key}
              fill={series.color}
              isAnimationActive={false}
              key={series.key}
              maxBarSize={22}
              name={series.label}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function MarginChart({ points }: { points: MonthPoint[] }) {
  return (
    <div className="h-[200px] w-full">
      <ResponsiveContainer height="100%" width="100%">
        <LineChart data={points} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          {grid}
          <XAxis
            axisLine={false}
            dataKey="label"
            minTickGap={8}
            padding={{ left: 16, right: 16 }}
            tick={AXIS_TICK}
            tickLine={false}
          />
          <YAxis
            axisLine={false}
            domain={[(low: number) => Math.min(0, Math.floor(low)), (high: number) => Math.max(0, Math.ceil(high))]}
            tick={AXIS_TICK}
            tickFormatter={(value: number) => `${value}%`}
            tickLine={false}
            width={48}
          />
          <ReferenceLine stroke="oklch(0.708 0 0)" y={0} />
          <Tooltip content={<MonthTooltip />} cursor={{ stroke: GRID_COLOR }} />
          <Line
            activeDot={{ r: 5 }}
            dataKey="margin"
            dot={{ r: 3, strokeWidth: 2, fill: 'white' }}
            isAnimationActive={false}
            stroke={MARGIN_COLOR}
            strokeWidth={2}
            type="linear"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function ActivityChart({ points }: { points: MonthPoint[] }) {
  return (
    <div className="h-[200px] w-full">
      <ResponsiveContainer height="100%" width="100%">
        <BarChart barCategoryGap="30%" barGap={2} data={points} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          {grid}
          {xAxis}
          <YAxis allowDecimals={false} axisLine={false} tick={AXIS_TICK} tickLine={false} width={32} />
          <Tooltip content={<MonthTooltip />} cursor={{ fill: 'oklch(0.97 0 0)' }} />
          <Bar dataKey="orders" fill={ORDERS_COLOR} isAnimationActive={false} maxBarSize={18} radius={[4, 4, 0, 0]} />
          <Bar
            dataKey="projects"
            fill={PROJECTS_COLOR}
            isAnimationActive={false}
            maxBarSize={18}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function TopList({
  rows,
  other,
  kind,
}: {
  rows: BreakdownRow[]
  other: BreakdownRow | null | undefined
  kind: 'manufacturer' | 'product'
}) {
  const shown = rows.filter((row) => row.uuid)
  if (!shown.length) return <p className="text-sm text-muted-foreground">None yet</p>
  const most = Math.max(...shown.map((row) => num(row.revenue)), 1)
  return (
    <ol className="space-y-3">
      {shown.map((row) => (
        <li className="min-w-0" key={row.key}>
          <div className="flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              {kind === 'manufacturer' ? (
                <Link
                  className="block truncate text-sm font-medium hover:underline"
                  params={{ manuId: row.uuid! }}
                  to="/manufacturers/$manuId"
                >
                  {row.label}
                </Link>
              ) : (
                <Link
                  className="block truncate text-sm font-medium hover:underline"
                  params={{ productId: row.uuid! }}
                  title={row.label}
                  to="/products/$productId"
                >
                  {row.label}
                </Link>
              )}
            </div>
            <span className="shrink-0 text-sm font-semibold tabular-nums">{formatMoney(row.revenue)}</span>
          </div>
          <div aria-hidden className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full"
              style={{ width: `${Math.max((num(row.revenue) / most) * 100, 2)}%`, background: SERIES[0].color }}
            />
          </div>
          <p className="mt-1 truncate text-xs text-muted-foreground tabular-nums">
            {[
              kind === 'product' ? row.detail : null,
              row.units != null ? `${formatNumber(row.units, 0)} ${row.units === 1 ? 'unit' : 'units'}` : null,
              row.margin != null ? `${formatMoney(row.profit)} profit · ${formatPercent(row.margin)}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </li>
      ))}
      {other ? (
        <li className="text-xs text-muted-foreground tabular-nums">
          {other.label} · {formatMoney(other.revenue)}
        </li>
      ) : null}
    </ol>
  )
}

function issueState(issue: PaymentIssue): { label: string; tone: StatusTone } {
  if (issue.kind === 'failed') return { label: 'Failed', tone: 'danger' }
  const days = issue.days_late ?? 0
  if (issue.kind === 'overdue') return { label: `${days} ${days === 1 ? 'day' : 'days'} overdue`, tone: 'danger' }
  return { label: `Paid ${days} ${days === 1 ? 'day' : 'days'} late`, tone: 'warning' }
}

function IssueSubject({ issue }: { issue: PaymentIssue }) {
  if (issue.invoice_id) {
    return (
      <Link
        className="font-mono font-medium hover:underline"
        params={{ invoiceId: issue.invoice_id }}
        to="/receivables/$invoiceId"
      >
        {issue.invoice_number ?? 'Invoice'}
      </Link>
    )
  }
  if (issue.order_uuid) {
    return (
      <Link className="font-medium hover:underline" params={{ orderId: issue.order_uuid }} to="/orders/$orderId">
        Order #{issue.order_number}
      </Link>
    )
  }
  return null
}

function HowTheyPay({ payments }: { payments: CustomerInsights['payments'] }) {
  const total = payments.methods.reduce((sum, row) => sum + num(row.amount), 0)
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="min-w-0">
        <h3 className="mb-3 text-sm font-semibold">Paid by</h3>
        {payments.methods.length ? (
          <>
            <div aria-hidden className="flex h-2.5 overflow-hidden rounded-full bg-muted">
              {payments.methods.map((row) => (
                <span
                  key={row.method}
                  style={{
                    width: `${total ? (num(row.amount) / total) * 100 : 100 / payments.methods.length}%`,
                    background: METHODS[row.method].color,
                  }}
                />
              ))}
            </div>
            <ul className="mt-3 divide-y">
              {payments.methods.map((row) => (
                <li className="flex items-center justify-between gap-3 py-2 text-sm" key={row.method}>
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      aria-hidden
                      className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                      style={{ background: METHODS[row.method].color }}
                    />
                    <span className="font-medium">{METHODS[row.method].label}</span>
                    <span className="text-muted-foreground">
                      {row.payments} {row.payments === 1 ? 'payment' : 'payments'}
                    </span>
                  </span>
                  <span className="shrink-0 font-semibold tabular-nums">{formatMoney(row.amount)}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No payments yet</p>
        )}
      </div>
      <div className="min-w-0">
        <h3 className="mb-3 text-sm font-semibold">Late or failed</h3>
        {payments.issues.length ? (
          <ul className="divide-y rounded-lg border">
            {payments.issues.map((issue, index) => {
              const state = issueState(issue)
              return (
                <li className="flex items-start justify-between gap-3 px-3 py-2.5 text-sm" key={`${issue.occurred_at}-${index}`}>
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <StatusPill tone={state.tone}>{state.label}</StatusPill>
                      <IssueSubject issue={issue} />
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {[
                        day.format(parseUTCDate(issue.occurred_at)),
                        issue.method ? METHODS[issue.method].label : null,
                        issue.reason ? sentenceCase(issue.reason) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums">{formatMoney(issue.amount)}</span>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">None</p>
        )}
      </div>
    </div>
  )
}

function daysToPay(average: string | null | undefined) {
  if (average == null) return null
  const days = Math.round(Number(average))
  return days < 1 ? 'Same day' : `${days} ${days === 1 ? 'day' : 'days'}`
}

/** What the customer buys from us, what we earn on them and how they pay, under the header. */
export function CustomerGlance({
  insights,
  projects,
}: {
  insights: CustomerInsights
  projects: CustomerPayments['projects']
}) {
  const totals = insights.summary.current
  const since = [
    ...insights.summary.buckets.filter((bucket) => bucket.orders).map((bucket) => bucket.start.slice(0, 7)),
    ...projects.map((project) => localMonth(project.created_at)),
  ].sort()[0]
  const lastOrder = [...insights.summary.buckets].reverse().find((bucket) => bucket.orders)
  const usual = [...insights.payments.methods].sort((a, b) => b.payments - a.payments)[0]
  const profitNegative = num(totals.profit) < 0
  const facts: { label: string; value: string | null }[] = [
    { label: 'Customer since', value: since ? monthLong.format(monthDate(since)) : null },
    { label: 'Last order', value: lastOrder ? monthLong.format(monthDate(lastOrder.start.slice(0, 7))) : null },
    { label: 'Usually pays by', value: usual ? METHODS[usual.method].label : null },
    { label: 'Days to pay', value: daysToPay(insights.payments.average_days_to_pay) },
  ]
  return (
    <section aria-label="Customer at a glance" className="overflow-hidden rounded-xl border bg-card">
      <div className="grid divide-y sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        <div className="px-4 py-4">
          <p className="text-sm text-muted-foreground">Revenue</p>
          <p className="mt-1 text-3xl font-semibold tracking-tight tabular-nums">{formatMoney(totals.revenue)}</p>
          <p className="mt-1 text-sm text-muted-foreground tabular-nums">
            {totals.orders} {totals.orders === 1 ? 'order' : 'orders'}
            {totals.average_order != null ? ` · ${formatMoney(totals.average_order)} average` : ''}
          </p>
        </div>
        <div className="px-4 py-4">
          <p className="text-sm text-muted-foreground">Profit</p>
          <p
            className={cn(
              'mt-1 text-3xl font-semibold tracking-tight tabular-nums',
              profitNegative && 'text-destructive',
            )}
          >
            {formatMoney(totals.profit)}
          </p>
          <p className="mt-1 text-sm text-muted-foreground tabular-nums">
            {totals.margin != null ? `${formatPercent(totals.margin)} margin · ` : ''}Net {formatMoney(totals.net)}
          </p>
        </div>
      </div>
      <dl className="grid grid-cols-2 border-t lg:grid-cols-4">
        {facts.map((fact, index) => (
          <div
            className={cn(
              'min-w-0 px-4 py-2.5',
              index % 2 === 1 && 'border-l',
              index >= 2 && 'max-lg:border-t',
              index === 2 && 'lg:border-l',
            )}
            key={fact.label}
          >
            <dt className="text-xs text-muted-foreground">{fact.label}</dt>
            <dd className={cn('truncate text-sm font-medium', !fact.value && 'font-normal text-muted-foreground')}>
              {fact.value ?? 'None yet'}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

/** The customer page's default tab: sales by month, what they buy and how they pay (USA-1305). */
export function CustomerOverview({
  insights,
  projects,
}: {
  insights: CustomerInsights
  projects: CustomerPayments['projects']
}) {
  const points = monthPoints(
    insights.summary.buckets,
    projects.map((project) => project.created_at),
  )
  const hasOrders = insights.summary.current.orders > 0 || num(insights.summary.current.revenue) !== 0
  return (
    <div className="space-y-4">
      {hasOrders ? (
        <>
          <Panel aside={<Legend items={SERIES} />} title="Sales by month">
            <SalesChart points={points} />
          </Panel>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Margin">
              <MarginChart points={points} />
            </Panel>
            <Panel
              aside={
                <Legend
                  items={[
                    { label: 'Orders', color: ORDERS_COLOR },
                    { label: 'Projects', color: PROJECTS_COLOR },
                  ]}
                />
              }
              title="Orders and projects"
            >
              <ActivityChart points={points} />
            </Panel>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Top manufacturers">
              <TopList kind="manufacturer" other={insights.manufacturers.other} rows={insights.manufacturers.rows} />
            </Panel>
            <Panel title="Top products">
              <TopList kind="product" other={insights.products.other} rows={insights.products.rows} />
            </Panel>
          </div>
        </>
      ) : (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">No orders yet</p>
      )}
      <Panel title="How they pay">
        <HowTheyPay payments={insights.payments} />
      </Panel>
    </div>
  )
}
